import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { Icon, UilCube } from '@iconscout/react-unicons';
import { AwaitingCountKey } from 'hooks/useAwaitingProcessingCounts';
import { FeatureRoles } from 'utils/roleAccess';

export interface Route {
  name: string;
  icon?: IconProp | string | string[];
  iconSet?: 'font-awesome' | 'feather' | 'unicons';
  pages?: Route[];
  path?: string;
  pathName?: string;
  flat?: boolean;
  topNavIcon?: string;
  dropdownInside?: boolean;
  active?: boolean;
  new?: boolean;
  hasNew?: boolean;
  requiredRole?: string[];
  awaitingCountKey?: AwaitingCountKey;
}

export interface RouteItems {
  label: string;
  horizontalNavLabel?: string;
  icon: Icon;
  labelDisabled?: boolean;
  pages: Route[];
  megaMenu?: boolean;
  active?: boolean;
}

export const routes: RouteItems[] = [
  {
    label: '',
    icon: UilCube,
    pages: [
      {
        name: 'Branch Inventory',
        icon: 'home',
        path: `/product/:branchDomain/`,
        pathName: '',
        active: true,
        requiredRole: [...FeatureRoles.inventorySearch]
      },
      {
        name: 'Change Approval',
        icon: 'check-circle',
        path: `/product/:branchDomain/for-approval`,
        pathName: 'for-approval',
        active: true,
        requiredRole: [...FeatureRoles.inventorySearch],
        awaitingCountKey: 'actualQuantityCountsPending'
      },
      {
        name: 'Barcode Lookup',
        icon: 'search',
        path: '/barcode-lookup',
        pathName: 'barcode-lookup',
        active: true,
        requiredRole: [...FeatureRoles.inventorySearch]
      },
      {
        name: 'Actual Quantity',
        icon: 'clipboard',
        path: '/actual-quantity/:branchDomain',
        pathName: 'actual-quantity',
        active: true,
        requiredRole: [...FeatureRoles.actualQuantity]
      },
      {
        name: 'Locations',
        icon: 'map-pin',
        path: '/locations',
        pathName: 'locations',
        active: true,
        requiredRole: [...FeatureRoles.inventorySearch]
      },
      {
        name: 'AI Assistant',
        icon: 'message-circle',
        path: '/ai-assistant',
        pathName: 'ai-assistant',
        active: true,
        requiredRole: [...FeatureRoles.aiQuery]
      },
      {
        name: 'Tasks',
        icon: 'check-square',
        path: '/tasks',
        pathName: 'tasks',
        active: true,
        requiredRole: [...FeatureRoles.tasks]
      },
      {
        name: 'Product Management',
        icon: 'box',
        active: true,
        pages: [
          {
            name: 'Categories',
            path: '/product-category-list',
            pathName: 'product-category-list',
            active: true,
            requiredRole: [...FeatureRoles.productCategoriesBrands]
          },
          {
            name: 'Brands',
            path: '/product-brand-list',
            pathName: 'product-brand-list',
            active: true,
            requiredRole: [...FeatureRoles.productCategoriesBrands]
          },
          {
            name: 'Products',
            path: '/product-list',
            pathName: 'product-list',
            active: true,
            requiredRole: [...FeatureRoles.products]
          },
          {
            name: 'Product Set',
            active: true,
            requiredRole: [...FeatureRoles.adminOnly],
            pages: [
              {
                name: 'New Product Set',
                path: '/product-set',
                pathName: 'product-set',
                active: true,
                requiredRole: [...FeatureRoles.adminOnly]
              },
              {
                name: 'Search Product Set',
                path: '/product-set-search',
                pathName: 'product-set-search',
                active: true,
                requiredRole: [...FeatureRoles.adminOnly]
              }
            ]
          },
          {
            name: 'Procurement Invoices',
            path: '/procurement-invoices',
            pathName: 'procurement-invoices',
            active: true,
            requiredRole: [...FeatureRoles.procurementInvoice],
            awaitingCountKey: 'procurementInvoicesForApproval'
          },
          {
            name: 'Procurement Returns',
            path: '/procurement-returns',
            pathName: 'procurement-returns',
            active: true,
            requiredRole: [...FeatureRoles.returns]
          }
        ]
      },
      {
        name: 'Customers',
        icon: 'users',
        active: true,
        requiredRole: [...FeatureRoles.customers],
        pages: [
          {
            name: 'Company List',
            path: '/company-list',
            pathName: 'company-list',
            active: true,
            requiredRole: [...FeatureRoles.customers]
          },
          {
            name: 'Customer List',
            path: '/customer-list',
            pathName: 'customer-list',
            active: true,
            requiredRole: [...FeatureRoles.customers]
          }
        ]
      },
      {
        name: 'Discount',
        icon: 'percent',
        path: '/discount',
        pathName: 'discount',
        active: true,
        requiredRole: [...FeatureRoles.adminOnly]
      },
      {
        name: 'Transfer Requests',
        icon: 'arrow-right',
        path: '/transfer-requests',
        pathName: 'transfer-requests',
        active: true,
        requiredRole: [...FeatureRoles.adminOnly]
      },
      {
        name: 'Business Admin',
        icon: 'settings',
        active: true,
        pages: [
          {
            name: 'Store Report',
            path: '/store-report',
            pathName: 'store-report',
            active: true,
            requiredRole: [...FeatureRoles.salesReport]
          },
          {
            name: 'Shop Report',
            path: '/shop-report',
            pathName: 'shop-report',
            active: true,
            requiredRole: [...FeatureRoles.vehicleRepairReport]
          },
          {
            name: 'Procurement Summary',
            path: '/procurement-summary',
            pathName: 'procurement-summary',
            active: true,
            requiredRole: [...FeatureRoles.procurementSummary]
          },
          {
            name: 'Supplier Payments',
            path: '/supplier-payments',
            pathName: 'supplier-payments',
            active: true,
            requiredRole: [...FeatureRoles.supplierPayments],
            awaitingCountKey: 'supplierPaymentsAwaiting'
          },
          {
            name: 'Expenses',
            path: '/expenses',
            pathName: 'expenses',
            active: true,
            requiredRole: [...FeatureRoles.expenses]
          },
          {
            name: 'Marketing',
            path: '/marketing',
            pathName: 'marketing',
            active: true,
            requiredRole: [...FeatureRoles.adminOnly]
          },
          {
            name: 'Users',
            path: '/user-list',
            pathName: 'user-list',
            active: true,
            requiredRole: [...FeatureRoles.adminOnly]
          }
        ]
      },
      {
        name: 'Human Resources',
        icon: 'briefcase',
        active: true,
        requiredRole: [...FeatureRoles.thirteenthMonth],
        pages: [
          {
            name: 'Employees',
            path: '/hr/employees',
            pathName: 'hr-employees',
            active: true,
            requiredRole: [...FeatureRoles.humanResources]
          },
          {
            name: 'Manage',
            path: '/hr/manage',
            pathName: 'hr-manage',
            active: true,
            requiredRole: [...FeatureRoles.humanResources]
          },
          {
            name: 'Timelog',
            path: '/timelog',
            pathName: 'timelog',
            active: true,
            requiredRole: [...FeatureRoles.timelog]
          },
          {
            name: 'Present Today',
            path: '/hr/present-today',
            pathName: 'hr-present-today',
            active: true,
            requiredRole: [...FeatureRoles.humanResources]
          },
          {
            name: 'Salary Overview',
            path: '/hr/salary-overview',
            pathName: 'hr-salary-overview',
            active: true,
            requiredRole: [...FeatureRoles.humanResources]
          },
          {
            name: '13th Month',
            path: '/hr/thirteenth-month',
            pathName: 'hr-thirteenth-month',
            active: true,
            requiredRole: [...FeatureRoles.thirteenthMonth]
          },
          {
            name: 'Holidays',
            path: '/hr/holidays',
            pathName: 'hr-holidays',
            active: true,
            requiredRole: [...FeatureRoles.humanResources]
          },
          {
            name: 'Leaves',
            path: '/hr/leaves',
            pathName: 'hr-leaves',
            active: true,
            requiredRole: [...FeatureRoles.humanResources]
          },
          {
            name: 'Cash Advances',
            path: '/hr/cash-advances',
            pathName: 'hr-cash-advances',
            active: true,
            requiredRole: [...FeatureRoles.humanResources]
          },
          {
            name: 'Loans',
            path: '/hr/loans',
            pathName: 'hr-loans',
            active: true,
            requiredRole: [...FeatureRoles.humanResources]
          },
          {
            name: 'Overtime Requests',
            path: '/hr/overtime-requests',
            pathName: 'hr-overtime-requests',
            active: true,
            requiredRole: [...FeatureRoles.humanResources]
          }
        ]
      },
      {
        name: 'Purchase Order',
        icon: 'shopping-bag',
        path: '/purchase-order',
        pathName: 'purchase-order',
        active: true,
        requiredRole: [...FeatureRoles.purchaseOrder],
        awaitingCountKey: 'purchaseOrdersAwaiting'
      },
      {
        name: 'Resupply',
        icon: 'refresh-cw',
        path: '/resupply',
        pathName: 'resupply',
        active: true,
        requiredRole: [...FeatureRoles.resupply]
      },
      {
        name: 'Customer Requests',
        icon: 'bell',
        path: '/customer-requests',
        pathName: 'customer-requests',
        active: true,
        requiredRole: [...FeatureRoles.resupply]
      },
      {
        name: 'Sales',
        icon: 'file-text',
        active: true,
        pages: [
          {
            name: 'Sales Invoice',
            path: '/sales-invoice',
            pathName: 'sales-invoice',
            active: true,
            requiredRole: [...FeatureRoles.salesInvoice]
          },
          {
            name: 'New Package Photos',
            path: '/new-package-photos',
            pathName: 'new-package-photos',
            active: true,
            requiredRole: [...FeatureRoles.recentlyApprovedPackagePhotos]
          },
          {
            name: 'New Transaction',
            path: '/new-sale-transaction',
            pathName: 'new-sale-transaction',
            active: true,
            requiredRole: [...FeatureRoles.newSaleTransaction]
          }
        ]
      },
      {
        name: 'Suppliers',
        icon: 'folder-plus',
        path: '/supplier-list',
        pathName: 'supplier-list',
        active: true,
        requiredRole: [...FeatureRoles.adminOnly]
      },
      {
        name: 'Gates',
        icon: 'torii-gate',
        path: '/gates',
        pathName: 'gates',
        active: true,
        requiredRole: [...FeatureRoles.gate]
      },
      {
        name: 'Vehicles',
        icon: 'truck',
        path: '/vehicles',
        pathName: 'vehicles',
        active: true,
        requiredRole: [...FeatureRoles.vehicles]
      },
      {
        name: 'Car Makes',
        icon: 'grid',
        path: '/car-makes',
        pathName: 'car-makes',
        active: true,
        requiredRole: [...FeatureRoles.vehicles]
      },
      {
        name: 'Services',
        icon: 'wrench',
        path: '/services',
        pathName: 'services',
        active: true,
        requiredRole: [...FeatureRoles.vehicles]
      },
      {
        name: 'System Admin',
        icon: 'sliders',
        active: true,
        pages: [
          {
            name: 'Activity Logs',
            path: '/activity-log',
            pathName: 'activity-log',
            active: true,
            requiredRole: [...FeatureRoles.activityLog]
          },
          {
            name: 'AI Logs',
            path: '/ai-call-logs',
            pathName: 'ai-call-logs',
            active: true,
            requiredRole: [...FeatureRoles.aiCallLogs]
          },
          {
            name: 'Product Searches',
            path: '/product-search-logs',
            pathName: 'product-search-logs',
            active: true,
            requiredRole: [...FeatureRoles.productSearchLogs]
          },
          {
            name: 'Shop Management',
            icon: 'settings',
            active: true,
            requiredRole: [...FeatureRoles.adminOnly],
            pages: [
              {
                name: 'Image Categories',
                path: '/vehicle-image-categories',
                pathName: 'vehicle-image-categories',
                active: true,
                requiredRole: [...FeatureRoles.adminOnly]
              }
            ]
          },
          {
            name: 'Branches',
            path: '/branch-details',
            pathName: 'branch-details',
            active: true,
            requiredRole: [...FeatureRoles.adminOnly]
          }
        ]
      }
    ]
  }
];
